import React, {useState} from 'react'
import {CardItem} from '../types'
import {
  STATUS,
  VALUE_INACTIVE,
  VALUE_CARD,
  VALUE_CARD_UPPER,
} from '../../../../Utils/Constant/WalletCard'
import moment from 'moment-timezone'
import {useI18n} from '../../../../Utils/_helpers/useI18n'
import {useTimezone} from '../../../../hooks/useTimezone'
import './WalletCardTemplateMobileCard.scss'
import { formatDateWithUserTimezone } from '../utils'

interface WalletCardTemplateMobileCardProps {
  card: CardItem
  onEdit: (card: CardItem) => void
  onView: (card: CardItem) => void
  onDelete: (card: CardItem) => void
  onSetDefault: (card: CardItem) => void
}

export const WalletCardTemplateMobileCard: React.FC<WalletCardTemplateMobileCardProps> = ({
  card,
  onEdit,
  onView,
  onDelete,
  onSetDefault,
}) => {
  const {t} = useI18n()
  const {userTimezone} = useTimezone()
  const [showActions, setShowActions] = useState(false)
  const [menuPosition, setMenuPosition] = useState<{top: number; right: number} | null>(null)

  const handleViewClick = () => {
    onView(card)
  }

  const handleActionClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()

    const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect()
    const top = rect.bottom + 8
    const right = Math.max(8, window.innerWidth - rect.right - 8)

    setMenuPosition({top, right})
    setShowActions((prev) => !prev)
  }

  return (
    <>
      <div className={`wallet-card-template-mobile-card ${showActions ? 'dropdown-open' : ''}`}>
        {/* Header with image and menu */}
        <div className='card-header-section'>
          <div
            className='d-flex align-items-center gap-4 flex-grow-1'
            style={{maxWidth: 'calc(100% - 60px)'}}
          >
            {/* Logo/Image - Much bigger */}
            <div className='card-image-container' onClick={handleViewClick}>
              <img src={card.imagePreviewUrl} alt='Card Preview' className='card-image' />
            </div>
            <div className='card-info-section d-flex align-items-center gap-2'>
              <h2 className='card-name text-truncate' title={card.cardName} onClick={handleViewClick}>
                {card.cardName}
              </h2>
              {card.isDefault && (
                <i className='bi bi-star-fill me-1 default-badge-icon' ></i>
              )}
            </div>
          </div>

          {/* Menu button */}
          <div className='position-relative'>
            <button
              className='btn btn-action-mobile'
              onClick={handleActionClick}
              aria-label='Actions menu'
              aria-expanded={showActions}
            >
              <i className='fas fa-ellipsis-v'></i>
            </button>
          </div>
        </div>

        {/* Card Type and Status in one line */}
        <div className='d-flex align-items-center justify-content-center gap-3 mb-4'>
          <span className={`card-type-badge ${card.type}`}>
            {card.type
              .replace(/([A-Z])/g, ' $1')
              .replace(/^./, (str) => str.toUpperCase())
              .trim()
              .replace(VALUE_CARD, VALUE_CARD_UPPER)}
          </span>
          <span className={`status-badge ${card.status === STATUS.ACTIVE ? 'active' : 'inactive'}`}>
            {card.status === STATUS.INACTIVE
              ? VALUE_INACTIVE
              : card.status?.charAt(0).toUpperCase() + card.status?.slice(1)}
          </span>
        </div>

        {/* Created and Updated info centered in one line */}
        <div className='d-flex align-items-center justify-content-center gap-4 text-sm text-muted'>
          <div className='d-flex align-items-center gap-2'>
            <i className='bi bi-calendar-plus text-muted'></i>
            <span>{formatDateWithUserTimezone(card?.createdAt || '', 'MMM DD, YYYY')}</span>
          </div>
          <div className='d-flex align-items-center gap-2'>
            <i className='bi bi-arrow-clockwise text-muted'></i>
            <span>{formatDateWithUserTimezone(card?.updatedAt || '', 'MMM DD, YYYY')}</span>
          </div>
        </div>
      </div>

      {/* Overlay dropdown menu - positioned outside the card */}
      {showActions && (
        <div className='actions-dropdown-overlay' onClick={() => setShowActions(false)}>
          <div
            className='actions-dropdown-menu'
            style={
              menuPosition
                ? {top: menuPosition.top, right: menuPosition.right, transform: 'none'}
                : undefined
            }
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className='action-item'
              onClick={() => {
                onView(card)
                setShowActions(false)
              }}
              title={t('COMMON.ACTIONS.DETAIL')}
            >
              <i className='fas fa-eye'></i>
            </button>
            <button
              className='action-item'
              onClick={() => {
                onEdit(card)
                setShowActions(false)
              }}
              title={t('COMMON.ACTIONS.EDIT')}
            >
              <i className='fas fa-edit'></i>
            </button>
            {!card.isDefault && (
              <button
                className={`action-item ${card.status !== STATUS.ACTIVE ? 'disabled' : ''}`}
                onClick={() => {
                  if (card.status === STATUS.ACTIVE) {
                    onSetDefault(card)
                    setShowActions(false)
                  }
                }}
                title='Set Default'
                disabled={card.status !== STATUS.ACTIVE}
              >
                <i className='fas fa-star'></i>
              </button>
            )}
            <button
              className='action-item text-danger'
              onClick={() => {
                onDelete(card)
                setShowActions(false)
              }}
              title={t('COMMON.ACTIONS.DELETE')}
            >
              <i className='fas fa-trash'></i>
            </button>
          </div>
        </div>
      )}
    </>
  )
}
